import { Link, NavLink, Outlet, useParams } from "react-router-dom";
import { Activity, AlertTriangle, ArrowRightLeft, BarChart3, Clock, Flag, GitPullRequest, Globe, History, LayoutDashboard, Loader2, Rocket, ScrollText, Settings, Shield, Sliders, Webhook, Zap } from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "@/lib/api";
import { cn } from "@/lib/utils";
import { PendingEnvBanner } from "@/components/PendingEnvBanner";

const TABS = [
  { to: "overview",    label: "Overview",    icon: LayoutDashboard },
  { to: "deployments", label: "Deployments", icon: Activity },
  { to: "analytics",   label: "Analytics",   icon: BarChart3 },
  { to: "previews",    label: "Previews",    icon: GitPullRequest },
  { to: "domains",     label: "Domains",     icon: Globe },
  { to: "env",         label: "Environments", icon: Sliders },
  { to: "rules",       label: "Rules",       icon: ArrowRightLeft },
  { to: "cron",        label: "Cron",        icon: Clock },
  { to: "flags",       label: "Flags",       icon: Flag },
  { to: "webhooks",    label: "Webhooks",    icon: Webhook },
  { to: "log-drains",  label: "Log drains",  icon: ScrollText },
  { to: "deploy-hooks", label: "Deploy hooks", icon: Zap },
  { to: "protection",  label: "Protection",  icon: Shield },
  { to: "activity",    label: "Activity",    icon: History },
  { to: "settings",    label: "Settings",    icon: Settings },
] as const;

export function ProjectLayout() {
  const { org, project } = useParams<{ org: string; project: string }>();
  const qc = useQueryClient();

  // Project existence gate. Without this, child routes (Deployments, Domains,
  // …) silently render their own empty states for orgs/projects the server
  // has already 404'd, which reads as "no data" rather than "no such project".
  const probe = useQuery({
    queryKey: ["project", org, project],
    queryFn: () => api.get(`/orgs/${org}/projects/${project}`),
    retry: (_count, err) => !(err instanceof ApiError && err.status === 404),
  });

  const redeploy = useMutation({
    mutationFn: () =>
      api.post<{ deploymentId: string }>(`/orgs/${org}/projects/${project}/redeploy`, {
        env: "production",
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["deployments", org, project] });
      qc.invalidateQueries({ queryKey: ["preview-deployments", org, project] });
    },
  });

  if (probe.isError && probe.error instanceof ApiError && (probe.error.status === 404 || probe.error.status === 403)) {
    return (
      <div className="container py-16 max-w-screen-md text-center">
        <AlertTriangle className="h-8 w-8 text-warning mx-auto" />
        <h1 className="mt-4 text-lg font-semibold">Project not found</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          <span className="font-mono">{org}/{project}</span> doesn't exist or you don't have access.
        </p>
        <Link to={`/orgs/${org}`} className="mt-4 inline-block text-sm text-accent hover:underline">
          ← Back to {org}
        </Link>
      </div>
    );
  }

  return (
    <div className="container py-6 max-w-screen-xl">
      <div className="mb-5 flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1">
            <Link to={`/orgs/${org}`} className="font-mono hover:text-foreground hover:underline">
              {org}
            </Link>
            <span className="text-subtle-foreground">/</span>
            <span className="font-mono">{project}</span>
          </div>
          <h1 className="text-xl font-semibold tracking-tight">{project}</h1>
          {redeploy.isError && (
            <div className="text-xs text-fail mt-1">
              {(redeploy.error as ApiError)?.message ?? "Redeploy failed"}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => redeploy.mutate()}
            disabled={redeploy.isPending}
            className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md border border-border bg-subtle hover:bg-[rgb(var(--of-canvas-inset))] text-sm font-medium shadow-of-sm disabled:opacity-50"
          >
            {redeploy.isPending ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Rocket className="h-3.5 w-3.5" />
            )}
            {redeploy.isPending ? "Queuing…" : "Redeploy"}
          </button>
        </div>
      </div>
      {/* 13 tabs never fit a phone width — horizontal scroll, hidden bar */}
      <nav className="of-tabstrip">
        {TABS.map((t) => (
          <NavLink
            key={t.to}
            to={t.to}
            className={({ isActive }) =>
              cn(
                "inline-flex shrink-0 items-center gap-1.5 h-9 px-2.5 text-sm whitespace-nowrap text-muted-foreground border-b-2 border-transparent hover:text-foreground transition-colors",
                isActive && "text-foreground border-[rgb(var(--of-fg-default))] font-semibold",
              )
            }
            end={t.to === "deployments"}
          >
            <t.icon className="h-3.5 w-3.5" />
            {t.label}
          </NavLink>
        ))}
      </nav>
      <div className="pt-6">
        {org && project && <PendingEnvBanner orgSlug={org} projectSlug={project} />}
        <Outlet />
      </div>
    </div>
  );
}
